import * as _ from 'lodash';
import { FormEvent, useEffect, useState } from 'react';
import { IIncidentReportLookupData } from '@nkr/models/index';
import { Button, FormGroup, FormControl, ControlLabel, Glyphicon, Alert, Col, Panel } from 'react-bootstrap';
import { IncidentStatusType } from '@nkr/enums/index';
import { IIncidentReportAddEdit } from './models';
import { FormGroupWrapper, ViewWrapper } from './styles';
import MaskedInput from 'react-maskedinput';
import Log from './log';
import usePrevious from '@nkr/services/use-previous';

interface IProps {
    show: boolean,
    formData: IIncidentReportAddEdit,
    lookupData: IIncidentReportLookupData,
    container: any,
    onSave: (statusType: number) => void,
    onSaveLogEntry: (id: number, incidentReportId: number, actionTaken: number, comments: string) => void,
    onDeleteLogEntry: (id: number, incidentReportId: number) => void,
    onFieldChange: (propertyId: string, value: any) => void,
    onCancel: () => void
}

const ViewIncidentDetails = (props: IProps) => {
    const [validation, setValidation] = useState({
        centerId: true,
        candidateFirstName: true,
        candidateLastName: true,
        incidentOriginTypeId: true,
        incidentCategoryTypeId: true,
        incidentStatusId: true,
        assignedMentorId: true,
        incidentDate: true,
        description: true
    });

    const previousShow = usePrevious(props.show);

    useEffect(() => {
        if (previousShow == false && props.show) {
            setValidation({
                centerId: true,
                candidateFirstName: true,
                candidateLastName: true,
                incidentOriginTypeId: true,
                incidentCategoryTypeId: true,
                incidentStatusId: true,
                assignedMentorId: true,
                incidentDate: true,
                description: true
            });
        }
    }, [props.show]);

    const handleSaveLogEntry = (id: number, incidentReportId: number, actionTaken: number, comments: string) => {
        props.onSaveLogEntry(id, incidentReportId, actionTaken, comments);
    };

    const handleFieldChange = (e: FormEvent<FormControl & HTMLInputElement>) => {
        let id = e.currentTarget.id;
        let value = e.currentTarget.value;

        props.onFieldChange(id, value);
    };

    const handleSave = (statusType: number) => {
        let { id,
            centerId,
            incidentCategoryTypeId,
            incidentOriginTypeId,
            candidateFirstName,
            candidateLastName,
            incidentStatusId,
            assignedMentorId,
            incidentDate,
            description
        } = props.formData;

        let updatedValidation = {
            centerId: centerId != 0,
            incidentCategoryTypeId: incidentCategoryTypeId != 0,
            incidentOriginTypeId: incidentOriginTypeId != 0,
            incidentStatusId: id > 0 ? incidentStatusId != 0 : true,
            assignedMentorId: assignedMentorId != 0,
            incidentDate: incidentDate != '',
            candidateFirstName: candidateFirstName != '',
            candidateLastName: candidateLastName != '',
            description: description != ''
        };

        let isValid =
            updatedValidation.centerId
            && updatedValidation.incidentCategoryTypeId
            && updatedValidation.incidentOriginTypeId
            && updatedValidation.incidentStatusId
            && updatedValidation.incidentDate
            && updatedValidation.candidateFirstName
            && updatedValidation.candidateLastName
            && updatedValidation.description;

        setValidation(updatedValidation);

        if (isValid) {
            props.onSave(statusType);
        }
    };

    const handleCancel = () => {
        props.onCancel();
    };

    if (props.formData == null && props.show == false)
        return null;

    const isNewIncident = props.formData != null ? props.formData.id == 0 ? true : false : true;

    const title = isNewIncident ? <>Add Incident Report</> : <>Edit Incident Report #{props.formData.id}</>;

    const centerList = _.sortBy(props.lookupData.memberCenters, item => item.alias);

    const isValid =
        validation.centerId
        && validation.incidentCategoryTypeId
        && validation.incidentOriginTypeId
        && validation.incidentStatusId
        && validation.incidentDate
        && validation.candidateFirstName
        && validation.candidateLastName
        && validation.description;

    return (
        <>
            <ViewWrapper>
                <div className='modal-container'>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 15 }}>
                        <div style={{ fontWeight: 500, fontSize: 25 }}>{title}</div>
                        <div><Glyphicon glyph='remove' onClick={handleCancel} /></div>
                    </div>

                    <Panel>
                        <Panel.Body>
                            {!isValid && (
                                <Alert bsStyle='danger' style={{ padding: '7.5px 15px' }}>
                                    **All Fields Required** &nbsp;&nbsp;
                                    Please correct the issues below...
                                </Alert>
                            )}

                            <FormGroupWrapper validationState={validation.candidateFirstName ? null : 'error'}>
                                <Col xs={12} lg={4} componentClass={ControlLabel}>Candidate First Name:</Col>
                                <Col xs={12} lg={8}>
                                    <FormControl
                                        id='firstName'
                                        style={{ width: 200 }}
                                        placeholder='Enter first name'
                                        value={props.formData.candidateFirstName}
                                        onChange={handleFieldChange}
                                    />
                                </Col>
                            </FormGroupWrapper>

                            <FormGroupWrapper validationState={validation.candidateLastName ? null : 'error'}>
                                <Col xs={12} lg={4} componentClass={ControlLabel}>Candidate Last Name:</Col>
                                <Col xs={12} lg={8}>
                                    <FormControl
                                        id='lastName'
                                        style={{ width: 200 }}
                                        placeholder='Enter last name'
                                        value={props.formData.candidateLastName}
                                        onChange={handleFieldChange}
                                    />
                                </Col>
                            </FormGroupWrapper>

                            <FormGroupWrapper validationState={validation.incidentOriginTypeId ? null : 'error'}>
                                <Col xs={12} lg={4} componentClass={ControlLabel}>Origin:</Col>
                                <Col xs={12} lg={8}>
                                    <FormControl
                                        id='originTypeId'
                                        componentClass='select'
                                        value={props.formData.incidentOriginTypeId}
                                        onChange={handleFieldChange}
                                    >
                                        <option value={0}>- Please Select -</option>
                                        {props.lookupData.incidentOriginTypeList.map((item, index) =>
                                            <option key={'st-' + index} value={item.id}>{item.description}</option>
                                        )}
                                    </FormControl>
                                </Col>
                            </FormGroupWrapper>

                            <FormGroupWrapper validationState={validation.centerId ? null : 'error'}>
                                <Col xs={12} lg={4} componentClass={ControlLabel}>Center:</Col>
                                <Col xs={12} lg={8}>
                                    <FormControl
                                        id='centerId'
                                        componentClass='select'
                                        value={props.formData.centerId}
                                        onChange={handleFieldChange}
                                    >
                                        <option value={0}>- Please Select -</option>
                                        {centerList.map((item, index) =>
                                            <option key={'st-' + index} value={item.id}>{item.alias}</option>
                                        )}
                                    </FormControl>
                                </Col>
                            </FormGroupWrapper>

                            <FormGroupWrapper validationState={validation.incidentCategoryTypeId ? null : 'error'}>
                                <Col xs={12} lg={4} componentClass={ControlLabel}>Category:</Col>
                                <Col xs={12} lg={8}>
                                    <FormControl
                                        id='categoryTypeId'
                                        componentClass='select'
                                        value={props.formData.incidentCategoryTypeId}
                                        onChange={handleFieldChange}
                                    >
                                        <option value={0}>- Please Select -</option>
                                        {props.lookupData.incidentCategoryTypeList.map((item, index) =>
                                            <option key={'st-' + index} value={item.id}>{item.description}</option>
                                        )}
                                    </FormControl>
                                </Col>
                            </FormGroupWrapper>

                            <FormGroupWrapper validationState={validation.assignedMentorId ? null : 'error'}>
                                <Col xs={12} lg={4} componentClass={ControlLabel}>Mentor:</Col>
                                <Col xs={12} lg={8}>
                                    <FormControl
                                        id='assignedMentorId'
                                        componentClass='select'
                                        value={props.formData.assignedMentorId}
                                        onChange={handleFieldChange}
                                    >
                                        <option value={0}>- Please Select -</option>
                                        {props.lookupData.mentorList.map((item, index) =>
                                            <option key={'ml-' + index} value={item.id}>{item.firstName}, {item.lastName}</option>
                                        )}
                                    </FormControl>
                                </Col>
                            </FormGroupWrapper>

                            <FormGroupWrapper validationState={validation.incidentDate ? null : 'error'}>
                                <Col xs={12} lg={4} componentClass={ControlLabel}>Incident Date:</Col>
                                <Col xs={12} lg={8}>
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <div style={{ marginRight: 5 }}>
                                            <MaskedInput
                                                id='incidentDate'
                                                mask='11/11/1111'
                                                placeholder='mm/dd/yyyy'
                                                className='form-control'
                                                value={props.formData.incidentDate}
                                                onChange={handleFieldChange}
                                            />
                                        </div>
                                    </div>
                                </Col>
                            </FormGroupWrapper>

                            {!isNewIncident && (
                                <>
                                    <FormGroupWrapper validationState={validation.incidentStatusId ? null : 'error'}>
                                        <Col xs={12} lg={4} componentClass={ControlLabel}>Status:</Col>
                                        <Col xs={12} lg={8}>
                                            <FormControl
                                                id='statusId'
                                                componentClass='select'
                                                value={props.formData.incidentStatusId}
                                                onChange={handleFieldChange}
                                            >
                                                <option value={0}>- Please Select -</option>
                                                {props.lookupData.incidentStatusList.map((item, index) =>
                                                    <option key={'st-' + index} value={item.id}>{item.description}</option>
                                                )}
                                            </FormControl>
                                        </Col>
                                    </FormGroupWrapper>
                                </>
                            )}

                            <FormGroupWrapper style={{ alignItems: 'flex-start', marginBottom: 0 }} validationState={validation.description ? null : 'error'}>
                                <Col xs={12} lg={4} componentClass={ControlLabel}>
                                    Description: {!isNewIncident && (
                                        <Glyphicon glyph='lock' style={{ color: 'darkred' }} title="The description can't be edited nor deleted" />
                                    )}</Col>
                                <Col xs={12} lg={8}>
                                    <FormGroup>
                                        <FormControl
                                            id='description'
                                            componentClass='textarea'
                                            bsSize='sm'
                                            style={{ height: 150, resize: 'none' }}
                                            value={props.formData.description}
                                            onChange={handleFieldChange}
                                            disabled={!isNewIncident ? true : false}
                                        />
                                    </FormGroup>
                                </Col>
                            </FormGroupWrapper>
                        </Panel.Body>
                    </Panel>

                    <div className='row' style={{ marginTop: 0, marginBottom: 10 }}>
                        <div className='col-xs-12 col-lg-12'>
                            <div style={{ textAlign: 'center' }}>
                                {isNewIncident ?
                                    <Button bsStyle='primary' style={{ marginRight: 5 }} onClick={() => handleSave(IncidentStatusType.Draft)}>Create New Incident</Button>
                                    :
                                    <Button bsStyle='primary' style={{ marginRight: 5 }} onClick={() => handleSave(props.formData.incidentStatusId)}>Update Incident</Button>
                                }

                                <Button onClick={handleCancel}>Cancel</Button>
                            </div>
                        </div>
                    </div>

                    <div className='row' style={{ marginTop: 0, marginBottom: 10, height: 10 }}></div>

                    {!isNewIncident && (
                        <Log
                            incidentReportId={props.formData.id}
                            logs={props.formData.logs}
                            statusList={props.lookupData.incidentStatusList}
                            actionList={props.lookupData.actionList}
                            container={props.container}
                            isMobile={false}
                            onSaveLogEntry={handleSaveLogEntry}
                            onDeleteLogEntry={props.onDeleteLogEntry}
                        />)}
                </div>
            </ViewWrapper>
        </>
    );
}

export default ViewIncidentDetails;